import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { formatInZone, useAdminTimezone } from './lib/time.js';
export { TimezoneProvider, useAdminTimezone, detectBrowserTimezone, formatInZone, } from './lib/time.js';
const PRESET_PATTERNS = {
    short: 'd MMM HH:mm',
    dateTime: 'd MMM yyyy, HH:mm',
    date: 'd MMM yyyy',
    time: 'HH:mm',
    // `MMMM`, а не `LLLL`: в русской локали `LLLL` — именительный падеж
    // («4 октябрь»), а после числа нужен родительный («4 октября»).
    long: 'EEEE, d MMMM yyyy, HH:mm zzz',
    shortNumeric: 'dd.MM, HH:mm',
    dateTimeNumeric: 'dd.MM.yyyy, HH:mm',
    timestamp: 'dd.MM.yyyy, HH:mm:ss',
    /** «12.10» — короткая дата в строке (например, «пауза до 12.10»). */
    dayMonthNumeric: 'dd.MM',
};
function relativePhrase(iso, now) {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t))
        return { text: '', isRelative: false };
    const diff = Math.abs(now - t);
    const sec = Math.round(diff / 1000);
    // Короткие русские сокращения («5 мин назад»): интерфейс на русском, а
    // сокращения не склоняются — без словаря форм для каждого числа.
    if (sec < 30)
        return { text: 'только что', isRelative: true };
    if (sec < 60 * 60)
        return { text: `${Math.round(sec / 60)} мин назад`, isRelative: true };
    if (sec < 60 * 60 * 24)
        return { text: `${Math.round(sec / 3600)} ч назад`, isRelative: true };
    if (sec < 60 * 60 * 24 * 7)
        return { text: `${Math.round(sec / 86_400)} дн. назад`, isRelative: true };
    // Older than a week — callers fall back to an absolute preset.
    return { text: '', isRelative: false };
}
/**
 * Timezone-aware timestamp. Renders `<time dateTime={iso}>` for screen
 * readers and copy-paste. With `relative`, re-renders every 30s while
 * mounted so short-form phrases («2 мин назад») stay accurate without a parent
 * re-render.
 */
export function Time({ iso, preset = 'short', relative = false, className, timeZone, zoneLabel, }) {
    const adminTz = useAdminTimezone();
    const tz = timeZone ?? adminTz;
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (!relative)
            return;
        const id = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(id);
    }, [relative]);
    if (!iso)
        return null;
    let text;
    if (relative) {
        const { text: phrase, isRelative } = relativePhrase(iso, now);
        text = isRelative ? phrase : formatInZone(iso, PRESET_PATTERNS.short, tz);
    }
    else {
        text = formatInZone(iso, PRESET_PATTERNS[preset], tz);
    }
    if (zoneLabel && text !== '' && !(relative && relativePhrase(iso, now).isRelative)) {
        text = `${text} ${zoneLabel}`;
    }
    return (_jsx("time", { dateTime: iso, className: className, children: text }));
}
Time.displayName = 'Time';
//# sourceMappingURL=time.js.map